import { useState, useEffect, useMemo } from "react";
import { Modal, Button, Input, message, Card, Tag, Spin, Divider, List, Alert, Checkbox, Select, Progress } from "antd";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "react-leaflet";
import { DndContext, DragOverlay, useDraggable, useDroppable, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Sparkles, MapPin, Bus, Target, Info, Plus, ChevronDown, ChevronRight, GripVertical, User, Eye, Route, UserX, Trash2 } from "lucide-react";
import PassengerModal from "./PassengerModal";

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const destinationIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background-color: #ef4444; width: 32px; height: 32px; border-radius: 50%; border: 4px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center;">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// Dynamic hotel icon with count and color
const createHotelIcon = (count, color = "#3b82f6", isAssigned = false) => L.divIcon({
  className: "custom-marker",
  html: `<div style="position: relative;">
    <div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; ${isAssigned ? 'opacity: 0.7;' : ''}">
      <span style="color: white; font-size: 10px; font-weight: bold;">${count}</span>
    </div>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const busStartIcon = (color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background-color: ${color}; width: 28px; height: 28px; border-radius: 4px; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/></svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Component to fix map size when rendered in modal
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    // Small delay to ensure modal is fully rendered
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

// Component to handle map clicks for selecting coordinates
function MapClickHandler({ onMapClick, isActive }) {
  useMapEvents({
    click: (e) => {
      if (isActive) {
        onMapClick([e.latlng.lat, e.latlng.lng]);
      }
    },
  });
  return null;
}

// Icon for selected destination (pulsating)
const selectedDestinationIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="position: relative;">
    <div style="background-color: #ef4444; width: 32px; height: 32px; border-radius: 50%; border: 4px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; animation: pulse 1.5s infinite;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
    </div>
    <style>
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
    </style>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// Colors for bus routes
const BUS_COLORS = [
  "#8b5cf6", // purple
  "#f97316", // orange
  "#10b981", // green
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#eab308", // yellow
  "#6366f1", // indigo
  "#14b8a6", // teal
];

// Helper para obtener valor ajustado de un campo (considera bajas/ausentes)
const getAdjustedValue = (p, fieldCamel, fieldSnake, absentCamel, absentSnake) => {
  // Primero intentar campos adjusted del backend
  const adjustedCamel = `adjusted${fieldCamel.charAt(0).toUpperCase()}${fieldCamel.slice(1)}`;
  const adjustedSnake = `adjusted_${fieldSnake}`;

  if (p[adjustedCamel] !== undefined && p[adjustedCamel] !== null) return p[adjustedCamel];
  if (p[adjustedSnake] !== undefined && p[adjustedSnake] !== null) return p[adjustedSnake];

  // Si no hay campo adjusted, calcular manualmente: valor - ausentes
  const value = p[fieldCamel] ?? p[fieldSnake] ?? 0;
  const absent = p[absentCamel] ?? p[absentSnake] ?? 0;
  return Math.max(0, value - absent);
};

// Funciones específicas para cada tipo
const getAdjustedAdults = (p) => getAdjustedValue(p, 'adultsCount', 'adults_count', 'adultsAbsentCount', 'adults_absent_count');
const getAdjustedMinors = (p) => getAdjustedValue(p, 'minorsCount', 'minors_count', 'minorsAbsentCount', 'minors_absent_count');
const getAdjustedHalfPrice = (p) => getAdjustedValue(p, 'halfPriceAdultsCount', 'half_price_adults_count', 'halfPriceAbsentAdultsCount', 'half_price_absent_adults_count');
const getAdjustedFree = (p) => getAdjustedValue(p, 'freeCount', 'free_count', 'freeAbsentCount', 'free_absent_count');

// Calcular butacas totales de un pasajero (bebés NO cuentan)
const getPassengerSeats = (p) => {
  return getAdjustedAdults(p) + getAdjustedMinors(p) + getAdjustedHalfPrice(p) + getAdjustedFree(p);
};

// Draggable Hotel component (for pending hotels - can be dropped to buses)
function DraggableHotel({ hotelGroup, busId, isExpanded, onToggle, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `hotel-${busId}-${hotelGroup.hotel.id}`,
    data: {
      type: 'hotel',
      busId,
      hotelId: hotelGroup.hotel.id,
      passengers: hotelGroup.passengers,
    },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  // Calcular totales usando las funciones helper (considera bajas)
  const adults = hotelGroup.passengers?.reduce((sum, p) => sum + getAdjustedAdults(p), 0) || 0;
  const minors = hotelGroup.passengers?.reduce((sum, p) => sum + getAdjustedMinors(p), 0) || 0;

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div className="flex flex-col p-2 bg-gray-50 rounded text-xs hover:bg-gray-100">
        <div className="flex items-center gap-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-1 flex items-center gap-1">
            <GripVertical size={12} className="text-gray-400" />
            <span className="flex-1 truncate font-medium">{hotelGroup.hotel?.name}</span>
            <span className="text-gray-500">A:{adults} M:{minors}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title={isExpanded ? "Contraer" : "Expandir"}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
        {/* Mostrar empresas cuando hay múltiples grupos */}
        {hotelGroup.groups && hotelGroup.groups.length > 1 && (
          <div className="text-[10px] text-gray-400 mt-1 ml-4">
            {hotelGroup.groups.map((g, idx) => (
              <div key={idx} className="truncate">{g.group?.company?.name}</div>
            ))}
          </div>
        )}
      </div>
      {isExpanded && children}
    </div>
  );
}

// Sortable Hotel component (for hotels inside buses - can be reordered and moved to other buses)
function SortableHotel({ hotelGroup, busId, isExpanded, onToggle, index, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `hotel-${busId}-${hotelGroup.hotel.id}`,
    data: {
      type: 'hotel',
      busId,
      hotelId: hotelGroup.hotel.id,
      passengers: hotelGroup.passengers,
      index,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Calcular totales usando las funciones helper (considera bajas)
  const adults = hotelGroup.passengers?.reduce((sum, p) => sum + getAdjustedAdults(p), 0) || 0;
  const minors = hotelGroup.passengers?.reduce((sum, p) => sum + getAdjustedMinors(p), 0) || 0;

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      <div className="flex flex-col p-2 bg-gray-50 rounded text-xs hover:bg-gray-100">
        <div className="flex items-center gap-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-1 flex items-center gap-1">
            <GripVertical size={12} className="text-gray-400" />
            <span className="bg-gray-200 text-gray-600 px-1.5 rounded text-[10px] font-bold">{index + 1}</span>
            <span className="flex-1 truncate font-medium">{hotelGroup.hotel?.name}</span>
            <span className="text-gray-500">A:{adults} M:{minors}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="p-1 hover:bg-gray-200 rounded"
            title={isExpanded ? "Contraer" : "Expandir"}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
        {/* Mostrar empresas cuando hay múltiples grupos */}
        {hotelGroup.groups && hotelGroup.groups.length > 1 && (
          <div className="text-[10px] text-gray-400 mt-1 ml-4">
            {hotelGroup.groups.map((g, idx) => (
              <div key={idx} className="truncate">{g.group?.company?.name}</div>
            ))}
          </div>
        )}
      </div>
      {isExpanded && children}
    </div>
  );
}

// Draggable Passenger component
function DraggablePassenger({ passenger, busId, onViewDetail }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `passenger-${busId}-${passenger.id}`,
    data: {
      type: 'passenger',
      busId,
      passenger,
    },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  // Usar funciones helper que consideran bajas
  const adults = getAdjustedAdults(passenger);
  const minors = getAdjustedMinors(passenger);
  const passengerName = passenger.passengerName || passenger.passenger_name || passenger.name || 'Sin nombre';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-1 p-1.5 ml-4 bg-white rounded text-xs hover:bg-blue-50 border border-gray-100"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-1 flex items-center gap-1">
        <GripVertical size={10} className="text-gray-400" />
        <User size={10} className="text-gray-400" />
        <span className="flex-1 truncate">{passengerName}</span>
        <span className="text-gray-400">A:{adults} M:{minors}</span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onViewDetail?.(passenger.id);
        }}
        className="p-1 hover:bg-blue-100 rounded"
        title="Ver detalle"
      >
        <Eye size={14} className="text-blue-500" />
      </button>
    </div>
  );
}

// Droppable zone for pending (unassigned) passengers
function PendingDropZone({ children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'pending',
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-colors rounded-lg ${isOver ? 'bg-orange-100 ring-2 ring-orange-400' : ''}`}
    >
      {children}
      {isOver && (
        <div className="text-center py-2 text-orange-600 font-medium text-sm">
          Soltar aquí para desasignar
        </div>
      )}
    </div>
  );
}

// Droppable Bus Column component
function BusDropZone({ bus, busIndex, children, onOptimizeRoute, onChangeDepartureHotel, onDeleteBus }) {
  const { isOver, setNodeRef } = useDroppable({
    id: bus.id,
  });

  const totalSeats = bus.busExcursion?.bus?.seats || 0;
  const occupiedSeats = totalSeats - (bus.availableSeats || 0);
  const busColor = BUS_COLORS[busIndex % BUS_COLORS.length];

  return (
    <div
      ref={setNodeRef}
      className={`border rounded-lg p-2 min-h-[150px] transition-colors ${isOver ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}
    >
      <div className="border-b pb-2 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: busColor }} />
            <span className="font-medium text-sm">{bus.busExcursion?.bus?.busSelectorName}</span>
          </div>
          <div className="flex items-center gap-1">
            {onOptimizeRoute && (
              <button
                onClick={() => onOptimizeRoute(bus.id)}
                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                title="Optimizar ruta"
              >
                <Route size={14} />
              </button>
            )}
            {onDeleteBus && (
              <button
                onClick={() => onDeleteBus(bus.id)}
                className="p-1 hover:bg-red-100 rounded text-red-500"
                title="Eliminar micro"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {bus.guide?.firstName} {bus.guide?.lastName || 'Sin guía'}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          <span className="text-green-600">{bus.availableSeats} libres</span>
          <span className="mx-1">|</span>
          <span className="text-orange-600">{occupiedSeats} ocupadas</span>
          <span className="mx-1">|</span>
          <span>{totalSeats} total</span>
        </div>
        <div
          className="mt-2 p-1.5 bg-gray-50 rounded border border-dashed border-gray-300 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
          onClick={() => onChangeDepartureHotel?.(bus.id)}
          title="Click para cambiar hotel de salida"
        >
          <div className="flex items-center gap-1 text-xs">
            <MapPin size={12} className={bus.departureHotel ? "text-green-600" : "text-orange-500"} />
            <span className="text-gray-600">Salida:</span>
            {bus.departureHotel ? (
              <span className="font-medium text-green-700 truncate flex-1">{bus.departureHotel.name}</span>
            ) : (
              <span className="text-orange-500 italic">Sin asignar</span>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-1 max-h-[250px] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function AutoAssignBusesModal({
  isOpen,
  onClose,
  excursionDetail,
  buses,
  passengersByHotel,
  onAutoAssign,
  onUpdateExcursionCoordinates,
  onUpdateBusDepartureHotel,
  onOpenAddBusModal,
  onAssignPassengersToBus,
  onOptimizeRoute,
  onUnassignAllPassengers,
  onUnassignPassengers,
  onDeleteBus,
  onRefresh,
}) {
  const [step, setStep] = useState("config"); // 'config' | 'result'
  const [isLoading, setIsLoading] = useState(false);
  const [editingCoordinates, setEditingCoordinates] = useState(false);
  const [newCoordinates, setNewCoordinates] = useState("");
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [tempSelectedCoords, setTempSelectedCoords] = useState(null);
  const [mapKey, setMapKey] = useState(0); // Force re-render of map
  const [selectingHotelForBus, setSelectingHotelForBus] = useState(null); // Hotel to assign as departure
  const [assigningPassengersFromHotel, setAssigningPassengersFromHotel] = useState(null); // Hotel for passenger selection (step 1)
  const [selectedPassengerIds, setSelectedPassengerIds] = useState([]); // Selected passenger IDs
  const [passengersToAssign, setPassengersToAssign] = useState([]); // Passengers ready to assign (step 2)
  const [isSelectBusModalOpen, setIsSelectBusModalOpen] = useState(false); // Step 2 modal
  const [expandedHotels, setExpandedHotels] = useState({}); // Track which hotels are expanded
  const [activeDragItem, setActiveDragItem] = useState(null); // Current dragging item
  const [editingPassengerId, setEditingPassengerId] = useState(null); // Passenger detail modal
  const [visibleBusRoutes, setVisibleBusRoutes] = useState('all'); // Filter: which bus routes to show
  const [visibleBusHotels, setVisibleBusHotels] = useState('all'); // Filter: which bus hotels to show
  const [customHotelOrder, setCustomHotelOrder] = useState({}); // Track custom hotel order per bus: { busId: [hotelId1, hotelId2, ...] }
  const [transferProgress, setTransferProgress] = useState({ active: false, current: 0, total: 0, action: '' }); // Progress for passenger transfers
  const [selectingDepartureForBusId, setSelectingDepartureForBusId] = useState(null); // Bus ID for departure hotel selection

  // Get all available hotels (from buses + pending) for departure selection
  const allHotelsForDeparture = useMemo(() => {
    const hotelsMap = new Map();

    if (passengersByHotel) {
      passengersByHotel.forEach((h) => {
        if (h.hotel?.id) {
          hotelsMap.set(h.hotel.id, h.hotel);
        }
      });
    }

    if (buses) {
      buses.forEach((bus) => {
        const passengersHotels = bus.passengersGroupedByHotels || bus.passengers_grouped_by_hotels || [];
        passengersHotels.forEach((h) => {
          if (h.hotel?.id) {
            hotelsMap.set(h.hotel.id, h.hotel);
          }
        });
        if (bus.departureHotel?.id) {
          hotelsMap.set(bus.departureHotel.id, bus.departureHotel);
        }
      });
    }

    return Array.from(hotelsMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [passengersByHotel, buses]);

  // Toggle hotel expansion
  const toggleHotelExpanded = (busId, hotelId) => {
    const key = `${busId}-${hotelId}`;
    setExpandedHotels(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle drag end - move passenger(s) to new bus or reorder within same bus
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);

    if (!over || !active.data.current) return;

    const { type, busId: fromBusId, passengers, passenger, hotelId, index: fromIndex } = active.data.current;

    // Check if dropping on a bus or on another hotel
    const overId = over.id;
    const overData = over.data?.current;

    // Determine target bus ID
    let toBusId = overId;
    if (overData?.busId) {
      toBusId = overData.busId;
    }

    // Progress callback for handlers
    const onProgress = (current, total) => {
      setTransferProgress(prev => ({ ...prev, current }));
    };

    // Dropping on pending zone - unassign passengers
    if (toBusId === 'pending' && fromBusId !== 'pending') {
      const passengersToUnassign = type === 'hotel' ? passengers : [passenger];

      if (passengersToUnassign.length > 0 && onUnassignPassengers) {
        setTransferProgress({ active: true, current: 0, total: passengersToUnassign.length, action: 'Desasignando' });
        try {
          await onUnassignPassengers(passengersToUnassign, onProgress);
          message.success(`${passengersToUnassign.length} pasajero(s) desasignado(s)`);
        } finally {
          setTransferProgress({ active: false, current: 0, total: 0, action: '' });
        }
      }
      return;
    }

    // Reordering within the same bus
    if (fromBusId === toBusId && type === 'hotel' && overData?.type === 'hotel') {
      const toIndex = overData.index;
      if (fromIndex !== toIndex && fromIndex !== undefined && toIndex !== undefined) {
        const bus = buses?.find(b => b.id === toBusId);
        const passengersHotels = bus?.passengersGroupedByHotels || bus?.passengers_grouped_by_hotels || [];
        const currentOrder = customHotelOrder[toBusId] || passengersHotels.map(h => h.hotel?.id);

        const newOrder = arrayMove(currentOrder, fromIndex, toIndex);
        setCustomHotelOrder(prev => ({ ...prev, [toBusId]: newOrder }));
        message.success("Orden de hoteles actualizado");
        return;
      }
    }

    // Moving to a different bus
    if (fromBusId !== toBusId && toBusId !== 'pending') {
      const passengersToMove = type === 'hotel' ? passengers : [passenger];

      if (passengersToMove.length > 0 && onAssignPassengersToBus) {
        setTransferProgress({ active: true, current: 0, total: passengersToMove.length, action: 'Moviendo' });
        try {
          await onAssignPassengersToBus(toBusId, passengersToMove, onProgress);
          message.success(`${passengersToMove.length} pasajero(s) movido(s)`);
        } finally {
          setTransferProgress({ active: false, current: 0, total: 0, action: '' });
        }
      }
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveDragItem(event.active.data.current);
  };

  // Handle map click to select coordinates
  const handleMapClick = (coords) => {
    if (editingCoordinates) {
      const coordString = `${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`;
      setNewCoordinates(coordString);
      setTempSelectedCoords(coords);
    }
  };

  // Handle assigning hotel as departure for a bus
  const handleAssignHotelToBus = async (busId) => {
    if (!selectingHotelForBus || !onUpdateBusDepartureHotel) return;

    await onUpdateBusDepartureHotel(busId, selectingHotelForBus.id);
    setSelectingHotelForBus(null);
    message.success("Hotel de salida asignado");
  };

  // Handle opening passenger selection modal - preselect all passengers
  const handleOpenPassengerSelection = (hotel) => {
    setAssigningPassengersFromHotel(hotel);
    setSelectedPassengerIds(hotel.passengers?.map(p => p.id) || []);
  };

  // Handle continuing to bus selection (step 2)
  const handleContinueToSelectBus = () => {
    const selectedPassengers = assigningPassengersFromHotel?.passengers?.filter(
      (p) => selectedPassengerIds.includes(p.id)
    ) || [];
    setPassengersToAssign(selectedPassengers);
    setAssigningPassengersFromHotel(null);
    setIsSelectBusModalOpen(true);
  };

  // Handle assigning selected passengers to a bus
  const handleAssignPassengers = async (busId) => {
    if (passengersToAssign.length === 0 || !onAssignPassengersToBus) return;

    await onAssignPassengersToBus(busId, passengersToAssign);
    setIsSelectBusModalOpen(false);
    setPassengersToAssign([]);
    setSelectedPassengerIds([]);
    message.success(`${passengersToAssign.length} pasajeros asignados al micro`);
  };

  // Parse coordinates from string "lat, lon" to [lat, lon]
  const parseCoordinates = (coordStr) => {
    if (!coordStr) return null;
    try {
      const parts = coordStr.split(",").map((p) => parseFloat(p.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return parts;
      }
    } catch (e) {}
    return null;
  };

  // Get excursion destination coordinates
  const destinationCoords = useMemo(() => {
    const coords = excursionDetail?.excursionTypePrices?.excursionType?.coordinates;
    return parseCoordinates(coords);
  }, [excursionDetail]);

  // Get hotels with passengers (pending assignment)
  const hotelsWithPassengers = useMemo(() => {
    if (!passengersByHotel) return [];
    return passengersByHotel
      .filter((h) => h.hotel?.coordinates)
      .map((h) => ({
        ...h,
        coords: parseCoordinates(h.hotel.coordinates),
      }))
      .filter((h) => h.coords);
  }, [passengersByHotel]);

  // Group pending hotels by zone (agrupando primero por hotel.id para unir grupos del mismo hotel)
  const hotelsByZone = useMemo(() => {
    if (!passengersByHotel) return {};

    // Paso 1: Agrupar por hotel.id (unir grupos del mismo hotel)
    const groupedByHotel = {};
    passengersByHotel.forEach((item) => {
      const hotelId = item.hotel?.id;
      if (!hotelId) return;

      if (!groupedByHotel[hotelId]) {
        groupedByHotel[hotelId] = {
          hotel: item.hotel,
          groups: [],
          passengers: []
        };
      }
      groupedByHotel[hotelId].groups.push({
        group: item.group,
        passengers: item.passengers
      });
      groupedByHotel[hotelId].passengers = [
        ...groupedByHotel[hotelId].passengers,
        ...(item.passengers || [])
      ];
    });

    // Paso 2: Agrupar por zona
    const grouped = {};
    Object.values(groupedByHotel).forEach((hotelData) => {
      const zone = hotelData.hotel?.zones || hotelData.hotel?.zone || 'Sin zona';
      if (!grouped[zone]) grouped[zone] = [];
      grouped[zone].push(hotelData);
    });

    // Sort zones: Aldrey -> Centro -> La Perla -> Sin zona
    const zoneOrder = ['Aldrey', 'Centro', 'La Perla', 'Sin zona'];
    const sortedGrouped = {};
    zoneOrder.forEach(zone => {
      if (grouped[zone]) {
        sortedGrouped[zone] = grouped[zone];
      }
    });
    // Add any other zones not in the predefined order
    Object.keys(grouped).forEach(zone => {
      if (!sortedGrouped[zone]) {
        sortedGrouped[zone] = grouped[zone];
      }
    });

    return sortedGrouped;
  }, [passengersByHotel]);

  // Get all hotels from buses (assigned passengers) with their bus color
  const allHotelsWithStatus = useMemo(() => {
    const hotelsMap = new Map();

    // Helper to count seats from passengers (usa getPassengerSeats que considera bajas)
    const countSeats = (passengers) => passengers?.reduce((sum, p) => sum + getPassengerSeats(p), 0) || 0;

    // First, add pending hotels (from passengersByHotel)
    if (passengersByHotel) {
      passengersByHotel.forEach((h) => {
        if (h.hotel?.coordinates) {
          const coords = parseCoordinates(h.hotel.coordinates);
          if (coords) {
            const pendingSeats = countSeats(h.passengers);
            hotelsMap.set(h.hotel.id, {
              ...h,
              coords,
              pendingSeats,
              assignedSeats: 0,
              totalSeats: pendingSeats,
              isAssigned: false,
              busColor: null,
              busName: null,
              busIds: [], // Track which buses have passengers from this hotel
            });
          }
        }
      });
    }

    // Then, add/update hotels from buses (assigned)
    if (buses) {
      buses.forEach((bus, busIndex) => {
        const busColor = BUS_COLORS[busIndex % BUS_COLORS.length];
        const passengersHotels = bus.passengersGroupedByHotels || bus.passengers_grouped_by_hotels || [];

        passengersHotels.forEach((hotelGroup) => {
          if (hotelGroup.hotel?.coordinates) {
            const coords = parseCoordinates(hotelGroup.hotel.coordinates);
            if (coords) {
              const assignedSeats = countSeats(hotelGroup.passengers);

              const existing = hotelsMap.get(hotelGroup.hotel.id);
              if (existing) {
                // Hotel has both pending and assigned - sum up total
                hotelsMap.set(hotelGroup.hotel.id, {
                  ...existing,
                  assignedSeats: (existing.assignedSeats || 0) + assignedSeats,
                  totalSeats: existing.pendingSeats + (existing.assignedSeats || 0) + assignedSeats,
                  assignedBusColor: busColor,
                  assignedBusName: bus.busExcursion?.bus?.busSelectorName,
                  busIds: [...(existing.busIds || []), bus.id],
                });
              } else {
                // Hotel is fully assigned (no pending)
                hotelsMap.set(hotelGroup.hotel.id, {
                  hotel: hotelGroup.hotel,
                  passengers: hotelGroup.passengers,
                  coords,
                  pendingSeats: 0,
                  assignedSeats,
                  totalSeats: assignedSeats,
                  isAssigned: true,
                  busColor,
                  busName: bus.busExcursion?.bus?.busSelectorName,
                  busIds: [bus.id],
                });
              }
            }
          }
        });
      });
    }

    return Array.from(hotelsMap.values());
  }, [passengersByHotel, buses]);

  // Get buses with departure hotels
  const busesWithDepartureHotels = useMemo(() => {
    if (!buses) return [];
    return buses
      .filter((b) => b.departureHotel?.coordinates)
      .map((b, index) => ({
        ...b,
        coords: parseCoordinates(b.departureHotel.coordinates),
        color: BUS_COLORS[index % BUS_COLORS.length],
      }));
  }, [buses]);

  // Get routes from buses with passengers (shown in both config and result views)
  const busRoutes = useMemo(() => {
    if (!buses) return [];

    return buses.map((bus, index) => {
      const color = BUS_COLORS[index % BUS_COLORS.length];
      const routePoints = [];

      // Start from departure hotel
      const departureCoords = bus.departureHotel?.coordinates ? parseCoordinates(bus.departureHotel.coordinates) : null;
      if (departureCoords) {
        routePoints.push(departureCoords);
      }

      // Add hotels from passengers (use custom order if available, otherwise sorted by route order)
      const passengersHotels = bus.passengersGroupedByHotels || bus.passengers_grouped_by_hotels || [];
      let orderedHotels;

      if (customHotelOrder[bus.id] && customHotelOrder[bus.id].length > 0) {
        // Use custom order
        const orderMap = new Map(customHotelOrder[bus.id].map((id, idx) => [id, idx]));
        orderedHotels = [...passengersHotels].sort((a, b) => {
          const orderA = orderMap.get(a.hotel?.id) ?? 999;
          const orderB = orderMap.get(b.hotel?.id) ?? 999;
          return orderA - orderB;
        });
      } else {
        // Sort hotels by their order in the route (from departure to destination)
        orderedHotels = [...passengersHotels].sort((a, b) => {
          if (!departureCoords || !destinationCoords) return 0;

          const coordsA = a.hotel?.coordinates ? parseCoordinates(a.hotel.coordinates) : null;
          const coordsB = b.hotel?.coordinates ? parseCoordinates(b.hotel.coordinates) : null;

          if (!coordsA && !coordsB) return 0;
          if (!coordsA) return 1;
          if (!coordsB) return -1;

          const routeVectorLat = destinationCoords[0] - departureCoords[0];
          const routeVectorLon = destinationCoords[1] - departureCoords[1];

          const progressA = ((coordsA[0] - departureCoords[0]) * routeVectorLat +
                            (coordsA[1] - departureCoords[1]) * routeVectorLon) /
                            (routeVectorLat * routeVectorLat + routeVectorLon * routeVectorLon);
          const progressB = ((coordsB[0] - departureCoords[0]) * routeVectorLat +
                            (coordsB[1] - departureCoords[1]) * routeVectorLon) /
                            (routeVectorLat * routeVectorLat + routeVectorLon * routeVectorLon);

          return progressA - progressB;
        });
      }

      orderedHotels.forEach((hotelGroup) => {
        if (hotelGroup.hotel?.coordinates) {
          const coords = parseCoordinates(hotelGroup.hotel.coordinates);
          if (coords) routePoints.push(coords);
        }
      });

      // End at destination
      if (destinationCoords) {
        routePoints.push(destinationCoords);
      }

      return {
        bus,
        color,
        points: routePoints,
        passengersCount: bus.busExcursion?.bus?.seats - (bus.availableSeats || 0),
      };
    }).filter((r) => r.points.length > 1);
  }, [buses, destinationCoords, customHotelOrder]);

  // Reset state only when modal opens, not when excursionDetail changes
  useEffect(() => {
    if (isOpen) {
      setStep("config");
      setAssignmentResult(null);
      setEditingCoordinates(false);
      setTempSelectedCoords(null);
      setCustomHotelOrder({}); // Reset custom order when modal opens
      const coords = excursionDetail?.excursionTypePrices?.excursionType?.coordinates || "";
      setNewCoordinates(coords);
      // Force map re-render when modal opens
      setMapKey(prev => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCancelEdit = () => {
    setEditingCoordinates(false);
    setTempSelectedCoords(null);
    const coords = excursionDetail?.excursionTypePrices?.excursionType?.coordinates || "";
    setNewCoordinates(coords);
  };

  const handleSaveCoordinates = async () => {
    if (!newCoordinates || !parseCoordinates(newCoordinates)) {
      message.error("Formato de coordenadas invalido. Use: latitud, longitud");
      return;
    }

    if (onUpdateExcursionCoordinates) {
      await onUpdateExcursionCoordinates(newCoordinates);
    }
    setEditingCoordinates(false);
    setTempSelectedCoords(null);
    message.success("Coordenadas actualizadas");
  };

  const handleAutoAssign = async () => {
    setIsLoading(true);
    try {
      const result = await onAutoAssign();
      setAssignmentResult(result);
      setStep("result");
    } catch (error) {
      message.error("Error al realizar el armado automatico");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptimizeAllRoutes = async () => {
    if (!buses || buses.length === 0 || !onOptimizeRoute) {
      message.warning("No hay micros para optimizar");
      return;
    }
    setIsLoading(true);
    try {
      for (const bus of buses) {
        await onOptimizeRoute(bus.id);
      }
      message.success("Todas las rutas optimizadas");
    } catch (error) {
      message.error("Error al optimizar rutas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (step === "result") {
      window.location.reload();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-purple-500" />
          <span>Armado Automatico de Micros</span>
        </div>
      }
      open={isOpen}
      onCancel={handleClose}
      width="95vw"
      style={{ top: 20 }}
      footer={null}
      styles={{ body: { padding: "16px", maxHeight: "calc(100vh - 120px)", overflowY: "auto" } }}
    >
      {step === "config" ? (
        <div className="space-y-4">
          {/* Drag & Drop Section - TOP */}
          <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {/* Pending Hotels Section - Grouped by Zone (Droppable for unassigning) */}
            <PendingDropZone>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={16} className="text-blue-500" />
                  <span className="font-medium">Hoteles sin asignar</span>
                  {passengersByHotel && passengersByHotel.length > 0 && (
                    <Tag color="blue">{passengersByHotel.length} hoteles</Tag>
                  )}
                  <span className="text-xs text-gray-400 ml-2">Arrastrar aquí para desasignar</span>
                </div>
                {passengersByHotel && passengersByHotel.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(hotelsByZone).map(([zoneName, hotels]) => (
                      <div key={zoneName} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-blue-200">
                          <Tag color="blue" className="m-0">{zoneName}</Tag>
                          <span className="text-xs text-gray-500">{hotels.length} hoteles</span>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          {hotels.map((hotelGroup) => {
                            const hotelKey = `pending-${hotelGroup.hotel?.id}`;
                            const isExpanded = expandedHotels[hotelKey];

                            return (
                              <DraggableHotel
                                key={hotelKey}
                                hotelGroup={hotelGroup}
                                busId="pending"
                                isExpanded={isExpanded}
                                onToggle={() => toggleHotelExpanded("pending", hotelGroup.hotel?.id)}
                              >
                                {hotelGroup.passengers?.map((passenger) => (
                                  <DraggablePassenger
                                    key={passenger.id}
                                    passenger={passenger}
                                    busId="pending"
                                    onViewDetail={setEditingPassengerId}
                                  />
                                ))}
                              </DraggableHotel>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center text-gray-500 text-sm">
                    No hay hoteles sin asignar
                  </div>
                )}
              </div>
            </PendingDropZone>

            {/* Micros Grid */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bus size={16} />
                <span className="font-medium">Micros Configurados</span>
              </div>
              {onOpenAddBusModal && (
                <Button size="small" type="link" icon={<Plus size={14} />} onClick={onOpenAddBusModal}>
                  Agregar micro
                </Button>
              )}
            </div>

            {/* Summary */}
            {buses && buses.length > 0 && (() => {
              const totalSeats = buses.reduce((sum, bus) => sum + (bus.busExcursion?.bus?.seats || 0), 0);
              const assignedSeats = buses.reduce((sum, bus) => {
                const busSeats = bus.busExcursion?.bus?.seats || 0;
                const available = bus.availableSeats || 0;
                return sum + (busSeats - available);
              }, 0);
              const pendingPassengers = passengersByHotel?.reduce((sum, hotel) => sum + (hotel.passengers?.length || 0), 0) || 0;
              const assignedPassengers = buses.reduce((sum, bus) => {
                const passengersHotels = bus.passengersGroupedByHotels || bus.passengers_grouped_by_hotels || [];
                return sum + passengersHotels.reduce((s, h) => s + (h.passengers?.length || 0), 0);
              }, 0);
              const totalPassengers = pendingPassengers + assignedPassengers;

              return (
                <div className="flex gap-4 mb-3 p-2 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Butacas totales:</span>
                    <Tag color="blue">{totalSeats}</Tag>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Butacas asignadas:</span>
                    <Tag color={assignedSeats === totalSeats ? "success" : "orange"}>{assignedSeats}</Tag>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Pasajeros totales:</span>
                    <Tag color="purple">{totalPassengers}</Tag>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Pendientes:</span>
                    <Tag color={pendingPassengers === 0 ? "success" : "warning"}>{pendingPassengers}</Tag>
                  </div>
                </div>
              );
            })()}

            {!buses || buses.length === 0 ? (
              <Tag color="warning">No hay micros creados</Tag>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-3">
                {buses.map((bus, busIndex) => {
                  const passengersHotels = bus.passengersGroupedByHotels || bus.passengers_grouped_by_hotels || [];

                  const hotelsByZoneInBus = {};
                  const zoneOrder = ['Aldrey', 'Centro', 'La Perla', 'Sin zona'];

                  passengersHotels.forEach((hotelGroup) => {
                    const zone = hotelGroup.hotel?.zones || hotelGroup.hotel?.zone || 'Sin zona';
                    if (!hotelsByZoneInBus[zone]) {
                      hotelsByZoneInBus[zone] = [];
                    }
                    hotelsByZoneInBus[zone].push(hotelGroup);
                  });

                  const sortedZones = Object.keys(hotelsByZoneInBus).sort((a, b) => {
                    const indexA = zoneOrder.indexOf(a);
                    const indexB = zoneOrder.indexOf(b);
                    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                  });

                  const sortableIds = passengersHotels.map(h => `hotel-${bus.id}-${h.hotel?.id}`);

                  return (
                    <BusDropZone
                      key={bus.id}
                      bus={bus}
                      busIndex={busIndex}
                      onOptimizeRoute={onOptimizeRoute}
                      onChangeDepartureHotel={(busId) => setSelectingDepartureForBusId(busId)}
                      onDeleteBus={(busId) => {
                        Modal.confirm({
                          title: '¿Eliminar este micro?',
                          content: (
                            <div>
                              <p>Se eliminará el micro <strong>{bus.busExcursion?.bus?.busSelectorName}</strong>.</p>
                              <p className="text-orange-600 mt-2">Esto desasignará todos los pasajeros y eliminará los gastos asociados.</p>
                            </div>
                          ),
                          okText: 'Sí, eliminar',
                          cancelText: 'Cancelar',
                          okButtonProps: { danger: true },
                          onOk: async () => {
                            if (onDeleteBus) {
                              await onDeleteBus(busId);
                            }
                          },
                        });
                      }}
                    >
                      {passengersHotels.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-4">
                          Sin pasajeros asignados
                        </div>
                      ) : (
                        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                          {sortedZones.map((zoneName) => (
                            <div key={zoneName} className="mb-2">
                              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1 border-b border-gray-200">
                                {zoneName}
                              </div>
                              {hotelsByZoneInBus[zoneName].map((hotelGroup) => {
                                const globalIndex = passengersHotels.findIndex(h => h.hotel?.id === hotelGroup.hotel?.id);
                                const hotelKey = `${bus.id}-${hotelGroup.hotel?.id}`;
                                const isExpanded = expandedHotels[hotelKey];

                                return (
                                  <SortableHotel
                                    key={hotelKey}
                                    hotelGroup={hotelGroup}
                                    busId={bus.id}
                                    isExpanded={isExpanded}
                                    onToggle={() => toggleHotelExpanded(bus.id, hotelGroup.hotel?.id)}
                                    index={globalIndex}
                                  >
                                    {hotelGroup.passengers?.map((passenger) => (
                                      <DraggablePassenger
                                        key={passenger.id}
                                        passenger={passenger}
                                        busId={bus.id}
                                        onViewDetail={setEditingPassengerId}
                                      />
                                    ))}
                                  </SortableHotel>
                                );
                              })}
                            </div>
                          ))}
                        </SortableContext>
                      )}
                    </BusDropZone>
                  );
                })}
              </div>

                <DragOverlay>
                  {activeDragItem && (
                    <div className="bg-white border-2 border-blue-400 rounded p-2 shadow-lg text-xs">
                      {activeDragItem.type === 'hotel' ? (
                        <span>🏨 {activeDragItem.passengers?.length} pasajeros</span>
                      ) : (
                        <span>👤 {activeDragItem.passenger?.passengerName || activeDragItem.passenger?.passenger_name || activeDragItem.passenger?.name}</span>
                      )}
                    </div>
                  )}
                </DragOverlay>
              </>
            )}
          </DndContext>

          {/* Action buttons */}
          <div className="flex gap-3 border-t pt-4">
            <Button
              type="default"
              size="large"
              danger
              onClick={() => {
                if (!onUnassignAllPassengers) {
                  message.warning("La función de desasignación no está disponible");
                  return;
                }
                Modal.confirm({
                  title: '¿Desasignar todos los pasajeros?',
                  content: 'Esta acción quitará a todos los pasajeros de los micros. Los pasajeros volverán a la lista de "sin asignar".',
                  okText: 'Sí, desasignar',
                  cancelText: 'Cancelar',
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    setIsLoading(true);
                    try {
                      await onUnassignAllPassengers();
                      message.success("Todos los pasajeros fueron desasignados");
                    } catch (error) {
                      message.error("Error al desasignar pasajeros");
                    } finally {
                      setIsLoading(false);
                    }
                  },
                });
              }}
              loading={isLoading}
              className="flex-1"
            >
              <UserX size={16} className="mr-2" />
              Desasignar Todos
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                const isDisabled = busesWithDepartureHotels.length === 0 || !destinationCoords;
                if (isDisabled) {
                  const reasons = [];
                  if (!destinationCoords) {
                    reasons.push("No hay coordenadas de destino definidas para este tipo de excursión");
                  }
                  if (busesWithDepartureHotels.length === 0) {
                    reasons.push("Ningún micro tiene hotel de salida asignado");
                  }
                  message.warning({
                    content: (
                      <div>
                        <strong>No se puede ejecutar el armado automático:</strong>
                        <ul className="mt-1 ml-4 list-disc">
                          {reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    ),
                    duration: 5,
                  });
                  return;
                }
                handleAutoAssign();
              }}
              loading={isLoading}
              className={`flex-1 ${busesWithDepartureHotels.length === 0 || !destinationCoords ? 'bg-gray-400 hover:bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              <Sparkles size={16} className="mr-2" />
              Asignacion automatica
            </Button>
          </div>

          {/* Map Section - BOTTOM */}
          <div className="border-t pt-4">
            {/* Map Filters */}
            <div className="flex gap-4 items-center mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rutas:</span>
                <Select
                  value={visibleBusRoutes}
                  onChange={setVisibleBusRoutes}
                  style={{ width: 180 }}
                  size="small"
                >
                  <Select.Option value="all">Todas las rutas</Select.Option>
                  {buses?.map((bus, i) => (
                    <Select.Option key={bus.id} value={bus.id}>
                      <span style={{ color: BUS_COLORS[i % BUS_COLORS.length] }}>●</span> {bus.busExcursion?.bus?.busSelectorName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Hoteles:</span>
                <Select
                  value={visibleBusHotels}
                  onChange={setVisibleBusHotels}
                  style={{ width: 180 }}
                  size="small"
                >
                  <Select.Option value="all">Todos los hoteles</Select.Option>
                  {buses?.map((bus, i) => (
                    <Select.Option key={bus.id} value={bus.id}>
                      <span style={{ color: BUS_COLORS[i % BUS_COLORS.length] }}>●</span> {bus.busExcursion?.bus?.busSelectorName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Map */}
            {editingCoordinates && (
              <div className="mb-2 p-2 bg-blue-100 border border-blue-300 rounded-lg text-center text-sm text-blue-700 font-medium">
                Modo seleccion activo - Haz click en el mapa para elegir el destino
              </div>
            )}
            <div className={`h-[500px] rounded-lg overflow-hidden border ${editingCoordinates ? 'border-blue-400 border-2' : ''}`}>
              <MapContainer
                center={destinationCoords || [-38.0055, -57.5426]}
                zoom={13}
                style={{ height: "100%", width: "100%", cursor: editingCoordinates ? "crosshair" : "grab" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapResizer />
                <MapClickHandler onMapClick={handleMapClick} isActive={editingCoordinates} />

                {/* Temporary selected destination marker (while editing) */}
                {editingCoordinates && tempSelectedCoords && (
                  <Marker position={tempSelectedCoords} icon={selectedDestinationIcon}>
                    <Popup>
                      <strong>Nueva ubicacion seleccionada</strong>
                      <br />
                      {tempSelectedCoords[0].toFixed(6)}, {tempSelectedCoords[1].toFixed(6)}
                    </Popup>
                  </Marker>
                )}

                {/* Destination marker (only show when not editing or no temp coords) */}
                {destinationCoords && (!editingCoordinates || !tempSelectedCoords) && (
                  <Marker position={destinationCoords} icon={destinationIcon}>
                    <Popup>
                      <strong>Destino:</strong> {excursionDetail?.excursionType?.name}
                    </Popup>
                  </Marker>
                )}

                {/* Hotels - all with status */}
                {allHotelsWithStatus
                  .filter(hotel => {
                    if (visibleBusHotels === 'all') return true;
                    // Show hotels with pending passengers or assigned to the selected bus
                    if (hotel.pendingSeats > 0) return true;
                    return hotel.busIds?.includes(visibleBusHotels);
                  })
                  .map((hotel) => {
                  const hasPending = hotel.pendingSeats > 0;
                  const hasAssigned = hotel.assignedSeats > 0;
                  // Color: blue if has pending, bus color if fully assigned
                  const color = hasPending ? "#3b82f6" : (hotel.busColor || hotel.assignedBusColor || "#9ca3af");

                  return (
                    <Marker
                      key={hotel.hotel?.id}
                      position={hotel.coords}
                      icon={createHotelIcon(hotel.totalSeats, color, !hasPending && hasAssigned)}
                    >
                      <Popup>
                        <strong>{hotel.hotel?.name}</strong>
                        <br />
                        <span className="font-medium">{hotel.totalSeats} butacas total</span>
                        {hasAssigned && (
                          <>
                            <br />
                            <span style={{ color: hotel.busColor || hotel.assignedBusColor }}>
                              ✓ {hotel.assignedSeats} en {hotel.busName || hotel.assignedBusName}
                            </span>
                          </>
                        )}
                        {hasPending && (
                          <>
                            <br />
                            <span className="text-blue-600">
                              ⏳ {hotel.pendingSeats} pendientes
                            </span>
                          </>
                        )}
                        <br />
                        <span className="text-xs text-gray-500">
                          Zona: {hotel.hotel?.zones || "N/A"}
                        </span>
                        {buses && buses.length > 0 && hasPending && (
                          <div className="flex flex-col gap-1 mt-2">
                            <Button
                              size="small"
                              type="primary"
                              block
                              onClick={() => handleOpenPassengerSelection(hotel)}
                            >
                              Asignar pasajeros a micro
                            </Button>
                            <Button
                              size="small"
                              type="default"
                              block
                              onClick={() => setSelectingHotelForBus(hotel.hotel)}
                            >
                              Asignar como salida de micro
                            </Button>
                          </div>
                        )}
                      </Popup>
                    </Marker>
                  );
                })}

                {/* Bus departure hotels */}
                {busesWithDepartureHotels.map((bus) => (
                  <Marker
                    key={bus.id}
                    position={bus.coords}
                    icon={busStartIcon(bus.color)}
                  >
                    <Popup>
                      <strong>{bus.busExcursion?.bus?.busSelectorName}</strong>
                      <br />
                      Sale desde: {bus.departureHotel?.name}
                      <br />
                      {bus.availableSeats} asientos disponibles
                    </Popup>
                  </Marker>
                ))}

                {/* Routes - show when buses have passengers assigned */}
                {busRoutes
                  .filter(route => visibleBusRoutes === 'all' || route.bus.id === visibleBusRoutes)
                  .map((route) => (
                    <Polyline
                      key={`route-${route.bus.id}`}
                      positions={route.points}
                      color={route.color}
                      weight={4}
                      opacity={0.7}
                    />
                  ))}
              </MapContainer>
            </div>
            <div className="mt-2 flex gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white" />
                Destino
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                Hotel pendiente
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-purple-500 border-2 border-white" />
                Salida de micro
              </div>
            </div>

            {/* Cards section - 2 columns */}
            <div className="grid grid-cols-2 gap-4 mt-4">
            <Card size="small" title={<div className="flex items-center gap-2"><Info size={16} /> Como funciona</div>}>
              <div className="text-xs space-y-1">
                <ol className="list-decimal list-inside space-y-1 text-gray-700">
                  <li><strong>Hotel de salida</strong>: Cada micro parte desde su hotel asignado</li>
                  <li><strong>Zonas</strong>: Recorre: Aldrey → Centro → La Perla</li>
                  <li><strong>Coordenadas</strong>: Optimiza hacia el destino</li>
                </ol>
                <p className="text-gray-500 pt-1 border-t">
                  Arrastra hoteles o pasajeros entre micros.
                </p>
              </div>
            </Card>

            <Card size="small" title={<div className="flex items-center gap-2"><Target size={16} /> Coordenadas Destino</div>}>
              <div className="space-y-2">
                <div className="text-sm text-gray-600">
                  <strong>{excursionDetail?.excursionType?.name}</strong>
                </div>
                {editingCoordinates ? (
                  <div className="space-y-2">
                    <Input
                      size="small"
                      value={newCoordinates}
                      onChange={(e) => setNewCoordinates(e.target.value)}
                      placeholder="ej: -38.0055, -57.5426"
                    />
                    <div className="flex gap-2">
                      <Button size="small" onClick={handleCancelEdit}>
                        Cancelar
                      </Button>
                      <Button size="small" type="primary" onClick={handleSaveCoordinates}>
                        Guardar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {destinationCoords ? `${destinationCoords[0]}, ${destinationCoords[1]}` : "No definidas"}
                    </code>
                    <Button size="small" onClick={() => setEditingCoordinates(true)}>
                      Editar
                    </Button>
                  </div>
                )}
              </div>
            </Card>
            </div>
          </div>
        </div>
      ) : (
        /* Result view with routes */
        <div className="flex gap-4">
          {/* Left side - Routes legend */}
          <div className="w-1/4 space-y-4">
            <Card size="small" title="Resultado del Armado">
              {assignmentResult?.error ? (
                <Tag color="error">{assignmentResult.error}</Tag>
              ) : assignmentResult?.unassignedCount > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message={`${assignmentResult.unassignedCount} pasajeros sin asignar`}
                  description="No hay suficiente capacidad en los micros. Agrega más micros o asigna manualmente."
                  className="text-sm"
                />
              ) : (
                <Tag color="success">{assignmentResult?.message || "Armado completado"}</Tag>
              )}
            </Card>

            <Card size="small" title="Rutas de Micros">
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {busRoutes.map((route, index) => (
                  <div key={route.bus.id} className="border-b pb-2 last:border-b-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: route.color }}
                      />
                      <span className="font-medium text-sm">
                        {route.bus.busExcursion?.bus?.busSelectorName}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 ml-6">
                      {route.bus.guide?.firstName} {route.bus.guide?.lastName}
                    </div>
                    <div className="text-xs text-gray-600 ml-6 mt-1">
                      {route.points.length - 1} paradas • {route.passengersCount} pasajeros
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Button type="primary" block onClick={handleClose}>
              Cerrar y Actualizar
            </Button>
          </div>

          {/* Right side - Map with routes */}
          <div className="w-3/4">
            <div className="h-[600px] rounded-lg overflow-hidden border">
              <MapContainer
                key={`map-result-${mapKey}`}
                center={destinationCoords || [-38.0055, -57.5426]}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapResizer />

                {/* Draw routes */}
                {busRoutes.map((route) => (
                  <Polyline
                    key={route.bus.id}
                    positions={route.points}
                    color={route.color}
                    weight={4}
                    opacity={0.8}
                  />
                ))}

                {/* Destination marker */}
                {destinationCoords && (
                  <Marker position={destinationCoords} icon={destinationIcon}>
                    <Popup>
                      <strong>Destino:</strong> {excursionDetail?.excursionType?.name}
                    </Popup>
                  </Marker>
                )}

                {/* Bus start points */}
                {busRoutes.map((route) => {
                  if (route.points.length > 0) {
                    return (
                      <Marker
                        key={`start-${route.bus.id}`}
                        position={route.points[0]}
                        icon={busStartIcon(route.color)}
                      >
                        <Popup>
                          <strong>{route.bus.busExcursion?.bus?.busSelectorName}</strong>
                          <br />
                          Inicio de ruta
                        </Popup>
                      </Marker>
                    );
                  }
                  return null;
                })}
              </MapContainer>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50">
          <Spin size="large" tip="Asignando pasajeros a micros..." />
        </div>
      )}

      {transferProgress.active && (
        <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl border min-w-[300px]">
            <div className="text-center mb-4">
              <Spin size="small" className="mr-2" />
              <span className="font-medium">{transferProgress.action} pasajeros...</span>
            </div>
            <Progress
              percent={Math.round((transferProgress.current / transferProgress.total) * 100)}
              status="active"
              strokeColor={{ from: '#108ee9', to: '#87d068' }}
            />
            <div className="text-center text-sm text-gray-500 mt-2">
              {transferProgress.current} de {transferProgress.total}
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleccionar micro al asignar hotel de salida */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-blue-500" />
            <span>Asignar hotel de salida</span>
          </div>
        }
        open={!!selectingHotelForBus}
        onCancel={() => setSelectingHotelForBus(null)}
        footer={null}
        width={500}
      >
        <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
          <span className="text-sm text-blue-700">
            Hotel: <strong>{selectingHotelForBus?.name}</strong>
          </span>
        </div>
        <p className="mb-4 text-gray-600 text-sm">
          Selecciona el micro que tendrá este hotel como punto de salida:
        </p>
        <List
          dataSource={buses || []}
          locale={{ emptyText: "No hay micros disponibles" }}
          renderItem={(bus, index) => (
            <List.Item
              className="cursor-pointer hover:bg-blue-50 transition-colors duration-150 rounded"
              onClick={() => handleAssignHotelToBus(bus.id)}
            >
              <div className="flex items-center gap-3 w-full">
                <div
                  className="w-6 h-6 rounded flex items-center justify-center"
                  style={{ backgroundColor: BUS_COLORS[index % BUS_COLORS.length] }}
                >
                  <Bus size={14} className="text-white" />
                </div>
                <div className="flex flex-col flex-1">
                  <span className="font-medium">
                    {bus.busExcursion?.bus?.busSelectorName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {bus.departureHotel ? (
                      <>Actual: {bus.departureHotel.name}</>
                    ) : (
                      <Tag color="warning" className="text-xs">Sin hotel de salida</Tag>
                    )}
                  </span>
                </div>
                <Tag color={bus.availableSeats > 0 ? "success" : "default"}>
                  {bus.availableSeats} asientos
                </Tag>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      {/* Modal paso 1: Seleccionar pasajeros del hotel */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Bus size={16} className="text-green-500" />
            <span>Seleccionar pasajeros</span>
          </div>
        }
        open={!!assigningPassengersFromHotel}
        onCancel={() => {
          setAssigningPassengersFromHotel(null);
          setSelectedPassengerIds([]);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => {
              setAssigningPassengersFromHotel(null);
              setSelectedPassengerIds([]);
            }}>
              Cancelar
            </Button>
            <Button
              type="primary"
              disabled={selectedPassengerIds.length === 0}
              onClick={handleContinueToSelectBus}
            >
              Continuar ({selectedPassengerIds.length} seleccionados)
            </Button>
          </div>
        }
        width={600}
      >
        <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
          <span className="text-sm text-green-700">
            Hotel: <strong>{assigningPassengersFromHotel?.hotel?.name}</strong>
          </span>
        </div>

        <div className="mb-3 pb-2 border-b">
          <Checkbox
            checked={selectedPassengerIds.length === assigningPassengersFromHotel?.passengers?.length}
            indeterminate={selectedPassengerIds.length > 0 && selectedPassengerIds.length < (assigningPassengersFromHotel?.passengers?.length || 0)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedPassengerIds(assigningPassengersFromHotel?.passengers?.map(p => p.id) || []);
              } else {
                setSelectedPassengerIds([]);
              }
            }}
          >
            Seleccionar todos
          </Checkbox>
        </div>

        <List
          dataSource={assigningPassengersFromHotel?.passengers || []}
          locale={{ emptyText: "No hay pasajeros" }}
          style={{ maxHeight: 400, overflowY: 'auto' }}
          renderItem={(passenger) => {
            const adults = getAdjustedAdults(passenger);
            const minors = getAdjustedMinors(passenger);
            const free = getAdjustedFree(passenger);

            return (
              <List.Item className="py-2">
                <div className="flex items-center justify-between w-full">
                  <Checkbox
                    checked={selectedPassengerIds.includes(passenger.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPassengerIds([...selectedPassengerIds, passenger.id]);
                      } else {
                        setSelectedPassengerIds(selectedPassengerIds.filter(id => id !== passenger.id));
                      }
                    }}
                  >
                    <div>
                      <span className="font-medium">{passenger.passengerName}</span>
                      {passenger.group?.company?.name && (
                        <span className="text-xs text-gray-500 ml-2">
                          ({passenger.group.company.name})
                        </span>
                      )}
                    </div>
                  </Checkbox>
                  <div className="text-xs text-gray-500">
                    {adults > 0 && <span className="mr-2">A: {adults}</span>}
                    {minors > 0 && <span className="mr-2">M: {minors}</span>}
                    {free > 0 && <span>L: {free}</span>}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </Modal>

      {/* Modal paso 2: Seleccionar micro destino */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Bus size={16} className="text-blue-500" />
            <span>Seleccionar micro destino</span>
          </div>
        }
        open={isSelectBusModalOpen}
        onCancel={() => {
          setIsSelectBusModalOpen(false);
          setPassengersToAssign([]);
        }}
        footer={null}
        width={500}
      >
        <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
          <span className="text-sm text-blue-700">
            <strong>{passengersToAssign.length}</strong> pasajeros seleccionados
          </span>
        </div>

        <List
          dataSource={buses || []}
          locale={{ emptyText: "No hay micros disponibles" }}
          renderItem={(bus, index) => {
            const totalSeats = passengersToAssign.reduce((sum, p) => sum + getPassengerSeats(p), 0);
            const hasEnoughSeats = bus.availableSeats >= totalSeats;

            return (
              <List.Item
                className="cursor-pointer hover:bg-blue-50 transition-colors duration-150 rounded"
                onClick={() => handleAssignPassengers(bus.id)}
              >
                <div className="flex items-center gap-3 w-full">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center"
                    style={{ backgroundColor: BUS_COLORS[index % BUS_COLORS.length] }}
                  >
                    <Bus size={14} className="text-white" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <span className="font-medium">
                      {bus.busExcursion?.bus?.busSelectorName}
                    </span>
                    <span className="text-xs text-gray-500">
                      Guía: {bus.guide?.firstName} {bus.guide?.lastName}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <Tag color={hasEnoughSeats ? "success" : "warning"}>
                      {bus.availableSeats} asientos libres
                    </Tag>
                    {!hasEnoughSeats && (
                      <span className="text-xs text-orange-500">
                        Faltan {totalSeats - bus.availableSeats} asientos
                      </span>
                    )}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </Modal>

      {/* Modal para seleccionar hotel de salida de un micro */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-green-500" />
            <span>Seleccionar hotel de salida</span>
          </div>
        }
        open={!!selectingDepartureForBusId}
        onCancel={() => setSelectingDepartureForBusId(null)}
        footer={null}
        width={500}
      >
        {(() => {
          const selectedBus = buses?.find(b => b.id === selectingDepartureForBusId);
          return (
            <>
              <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
                <span className="text-sm text-green-700">
                  Micro: <strong>{selectedBus?.busExcursion?.bus?.busSelectorName}</strong>
                </span>
                {selectedBus?.departureHotel && (
                  <div className="text-xs text-gray-500 mt-1">
                    Actual: {selectedBus.departureHotel.name}
                  </div>
                )}
              </div>
              <p className="mb-4 text-gray-600 text-sm">
                Selecciona el hotel desde donde saldrá este micro:
              </p>
              <div className="max-h-[400px] overflow-y-auto">
                <List
                  dataSource={allHotelsForDeparture}
                  locale={{ emptyText: "No hay hoteles disponibles" }}
                  renderItem={(hotel) => {
                    const isCurrentDeparture = selectedBus?.departureHotel?.id === hotel.id;
                    return (
                      <List.Item
                        className={`cursor-pointer hover:bg-green-50 transition-colors duration-150 rounded ${isCurrentDeparture ? 'bg-green-100' : ''}`}
                        onClick={async () => {
                          if (onUpdateBusDepartureHotel) {
                            if (isCurrentDeparture) {
                              await onUpdateBusDepartureHotel(selectingDepartureForBusId, null);
                              message.success('Hotel de salida desasignado');
                            } else {
                              await onUpdateBusDepartureHotel(selectingDepartureForBusId, hotel.id);
                              message.success(`Hotel de salida actualizado: ${hotel.name}`);
                            }
                          }
                          setSelectingDepartureForBusId(null);
                        }}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <MapPin size={16} className={isCurrentDeparture ? "text-green-600" : "text-gray-400"} />
                          <div className="flex flex-col flex-1">
                            <span className={`font-medium ${isCurrentDeparture ? 'text-green-700' : ''}`}>
                              {hotel.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {hotel.zones || hotel.zone || 'Sin zona'}
                            </span>
                          </div>
                          {isCurrentDeparture && (
                            <Tag color="warning">Click para quitar</Tag>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Modal de detalle/edición de pasajero */}
      <PassengerModal
        open={!!editingPassengerId}
        onClose={() => {
          setEditingPassengerId(null);
          if (onRefresh) onRefresh();
        }}
        passengerId={editingPassengerId}
        onSuccess={() => {
          setEditingPassengerId(null);
          if (onRefresh) onRefresh();
        }}
      />
    </Modal>
  );
}

export default AutoAssignBusesModal;
